import { useIntl } from 'react-intl'

/**
 * Returns a localised property category label for homes & villas.
 *
 * To add a new prefixed category, add the ENTIRE translated phrase
 * (e.g. "Entire treehouse") as a new `case` below.
 * Do NOT try to concatenate a translated "Entire" prefix with the category —
 * other languages have different grammar, word order, and gender agreement
 * that make prefix concatenation unreliable.
 */
function usePropertyCategory(category?: string): string {
  const intl = useIntl()

  switch (category) {
    case 'Cabin':
      return intl.formatMessage({
        id: 'L6hwAU1MBc',
        defaultMessage: 'Entire cabin',
        description: 'Property category label for an entire cabin rental',
      })
    case 'Campsite':
      return intl.formatMessage({
        id: '7mtGZE3Y6Z',
        defaultMessage: 'Entire campsite',
        description: 'Property category label for an entire campsite rental',
      })
    case 'Chalet':
      return intl.formatMessage({
        id: 'T+wkH3VqFT',
        defaultMessage: 'Entire chalet',
        description: 'Property category label for an entire chalet rental',
      })
    case 'Condominium':
      return intl.formatMessage({
        id: '1vgTPLsv2+',
        defaultMessage: 'Entire condominium',
        description: 'Property category label for an entire condominium rental',
      })
    case 'Country House':
      return intl.formatMessage({
        id: 'YvhfHB0pWF',
        defaultMessage: 'Entire country house',
        description: 'Property category label for an entire country house rental',
      })
    case 'Guesthouse':
      return intl.formatMessage({
        id: 'gcrSayDt9U',
        defaultMessage: 'Entire guesthouse',
        description: 'Property category label for an entire guesthouse rental',
      })
    case 'Apartment':
      return intl.formatMessage({
        id: 'BOLOk/kBxW',
        defaultMessage: 'Entire apartment',
        description: 'Property category label for an entire apartment rental',
      })
    case 'Private vacation home':
      return intl.formatMessage({
        id: 'FC2DdBGOaR',
        defaultMessage: 'Entire private vacation home',
        description: 'Property category label for an entire private vacation home rental',
      })
    case 'Villa':
      return intl.formatMessage({
        id: 'nPwjbi9wGU',
        defaultMessage: 'Entire villa',
        description: 'Property category label for an entire villa rental',
      })
    case 'Castle':
      return intl.formatMessage({
        id: 'mV37fLXSek',
        defaultMessage: 'Entire castle',
        description: 'Property category label for an entire castle rental',
      })
    default:
      if (category) return category
      return intl.formatMessage({
        id: 'bWZRSRX8cy',
        defaultMessage: 'Entire villa',
        description: 'Default property category label when no category is specified',
      })
  }
}

export default usePropertyCategory
